import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { invoiceService, updateInvoiceStatus } from '../services/firebase';
import { Invoice } from '../types';
import LoadingSpinner from '../components/LoadingSpinner';
import LoadError from '../components/LoadError';
import ConfirmDialog from '../components/ConfirmDialog';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { downloadInvoicePdf } from '../utils/invoicePdf';

/** Firestore may hand back a Timestamp, a Date, or a string. */
function formatDate(value: unknown): string {
  if (!value) return 'N/A';
  const raw =
    typeof value === 'object' && value !== null && 'toDate' in value
      ? (value as { toDate: () => Date }).toDate()
      : new Date(value as string | number | Date);
  return isNaN(raw.getTime()) ? 'N/A' : raw.toLocaleDateString();
}

export default function Invoices() {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [confirmingPayment, setConfirmingPayment] = useState<string | null>(null);
  // Marking an invoice paid stamps a payment date and is not something you want
  // to do by mis-clicking, so it goes through a confirmation step.
  const [pendingConfirm, setPendingConfirm] = useState<Invoice | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const handleDownload = async (invoice: Invoice) => {
    setDownloadingId(invoice.id);
    try {
      await downloadInvoicePdf(invoice);
    } catch (err) {
      console.error('Error generating invoice PDF:', err);
      toast.error('Could not generate the PDF. Please try again.');
    } finally {
      setDownloadingId(null);
    }
  };

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await invoiceService.getAllInvoices();
      setInvoices(data);
    } catch (err) {
      console.error("Failed to fetch invoices:", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleConfirmPayment = async (invoiceId: string) => {
    setPendingConfirm(null);
    setConfirmingPayment(invoiceId);
    try {
      await updateInvoiceStatus(invoiceId, 'paid', new Date());
      toast.success('Payment confirmed.');
      const updatedInvoices = await invoiceService.getAllInvoices();
      setInvoices(updatedInvoices);
    } catch (error) {
      console.error('Error confirming payment:', error);
      toast.error('Failed to confirm payment');
    } finally {
      setConfirmingPayment(null);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold">Invoices</h1>
        <Link to="/invoices/new" className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded">
          Create Invoice
        </Link>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <LoadError subject="invoices" onRetry={fetchInvoices} />
      ) : invoices.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-md">
          <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No invoices</h3>
          <p className="mt-1 text-sm text-gray-500">Get started by creating a new invoice.</p>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {invoices.map((invoice) => (
              <li key={invoice.id} className="px-4 py-4 sm:px-6">
                {/* The row body links through; action buttons live outside the
                    link so they aren't interactive elements nested in an anchor. */}
                <Link to={`/invoices/${invoice.id}`} className="block -mx-2 rounded px-2 py-1 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-blue-600 truncate">{invoice.invoiceNumber}</p>
                    <div className="ml-2 flex-shrink-0 flex items-center space-x-2">
                      <p className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        invoice.status === 'paid' ? 'bg-green-100 text-green-800' :
                        invoice.status === 'overdue' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {invoice.status}
                      </p>
                      {invoice.paymentData && (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          {invoice.paymentData.paymentMethod === 'cash' ? 'Cash' : 'Bank Transfer'}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="mt-2 sm:flex sm:justify-between">
                    <div className="sm:flex">
                      <p className="flex items-center text-sm text-gray-500">
                        {invoice.customer?.name || <span className="italic text-gray-400">No customer</span>}
                      </p>
                    </div>
                    <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                      <p>
                        Total: <span className="font-bold">${invoice.total?.toFixed(2) || '0.00'}</span>
                      </p>
                      <p className="ml-4">
                        Due: {formatDate(invoice.dueDate)}
                      </p>
                    </div>
                  </div>
                </Link>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => handleDownload(invoice)}
                    disabled={downloadingId === invoice.id}
                    className="inline-flex items-center gap-1.5 rounded border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4" />
                    {downloadingId === invoice.id ? 'Preparing…' : 'Download PDF'}
                  </button>
                </div>

                {/* Payment Information */}
                {invoice.paymentData && invoice.status === 'pending' && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-700">
                          Payment Method: {invoice.paymentData.paymentMethod === 'cash' ? 'Cash' : 'Bank Transfer'}
                        </p>
                        {invoice.paymentData.paymentMethod === 'bank-transfer' && invoice.paymentData.receiptImageUrl && (
                          <div className="mt-2">
                            <p className="text-sm text-gray-600 mb-1">Receipt Image:</p>
                            <img
                              src={invoice.paymentData.receiptImageUrl}
                              alt="Payment receipt"
                              className="h-16 w-auto object-contain border rounded"
                            />
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => setPendingConfirm(invoice)}
                        disabled={confirmingPayment === invoice.id}
                        className="shrink-0 rounded bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                      >
                        {confirmingPayment === invoice.id ? 'Confirming…' : 'Confirm Payment'}
                      </button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <ConfirmDialog
        open={Boolean(pendingConfirm)}
        title="Confirm this payment?"
        tone="primary"
        confirmLabel="Yes, mark as paid"
        message={
          pendingConfirm ? (
            <>
              This marks invoice <strong>{pendingConfirm.invoiceNumber}</strong> for{' '}
              <strong>{pendingConfirm.customer?.name || 'this customer'}</strong> as paid in full
              (${pendingConfirm.total?.toFixed(2) || '0.00'}) and stamps today&apos;s date as the
              payment date. Only do this once the money has actually cleared.
            </>
          ) : null
        }
        onConfirm={() => pendingConfirm && handleConfirmPayment(pendingConfirm.id)}
        onCancel={() => setPendingConfirm(null)}
      />
    </div>
  );
}
